import React, { useContext, useState, useEffect } from 'react';
import { ChevronLeft, AlertCircle, CheckCircle, Loader2, Building2, Shield } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import superAdminServices from '../../../../services/superAdminServices';
import AuthContext from '../../../../context/AuthContext';

export default function UpdateOrganization() {
  const { user, authTokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [organization, setOrganization] = useState(null);

  const [formData, setFormData] = useState({
    organisation_name: '',
    organisation_description: '',
    is_active: true,
    deactivation_reason: ''
  });

  // Fetch organization details on mount
  useEffect(() => {
    fetchOrganizationDetails();
  }, [id]);

  const fetchOrganizationDetails = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      const response = await superAdminServices.getOrganizationDetails(authTokens.access, id);
      const orgData = response.organization;
      
      setOrganization(orgData);
      setFormData({
        organisation_name: orgData.organisation_name || '',
        organisation_description: orgData.organisation_description || '',
        is_active: orgData.is_active !== undefined ? orgData.is_active : true,
        deactivation_reason: orgData.deactivation_reason || ''
      });
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des détails de l\'organisation');
      console.error('Error fetching organization:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear deactivation reason when reactivating
    if (name === 'is_active' && checked) {
      setFormData(prev => ({
        ...prev,
        deactivation_reason: ''
      }));
    }

    // Clear error when user makes changes
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    // Check organization name
    if (!formData.organisation_name.trim()) {
      setError('Le nom de l\'organisation est requis');
      return false;
    }
    
    // If deactivating, reason is required
    if (!formData.is_active && !formData.deactivation_reason.trim()) {
      setError('Une raison de désactivation est requise');
      return false;
    }

    setError('');
    return true;
  };

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      setLoading(true);
      setError("");
      setSuccessMessage("");

      try {
        // Prepare data for backend
        const payload = {
          organization_id: parseInt(id),
          organisation_name: formData.organisation_name.trim(),
          organisation_description: formData.organisation_description.trim(),
          is_active: formData.is_active,
        };

        if (!formData.is_active && formData.deactivation_reason.trim()) {
          payload.deactivation_reason = formData.deactivation_reason.trim();
        }

        console.log("📤 Sending payload:", payload);

        // Call service
        const result = await superAdminServices.updateOrganization(authTokens.access, payload);

        if (!result.success) {
          // Show backend error message
          setError(result.error || "Échec de la mise à jour de l'organisation.");
          return;
        }

        // ✅ Success
        setSuccessMessage("Organisation mise à jour avec succès !");
        setOrganization(result.data.organization);

        // Redirect after short delay
        setTimeout(() => navigate("/admin-panel/organizations"), 2000);

      } catch (err) {
        console.error("❌ Unexpected error:", err);
        setError(err.message || "Une erreur est survenue lors de la mise à jour de l'organisation.");
      } finally {
        setLoading(false);
      }
    };


  const handleCancel = () => {
    navigate(-1);
  };

  if (loadingData) {
    return (
      <div className="p-5 sm:p-6 max-w-6xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-2 text-sm text-gray-600">Chargement des détails de l'organisation...</span>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-5 sm:p-6 max-w-6xl mx-auto w-full">
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Organisation non trouvée</p>
          <button
            onClick={() => navigate('/admin-panel/organizations')}
            className="mt-4 px-4 py-2 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 max-w-6xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={handleCancel}
          className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Modifier l'organisation</h1>
          <p className="text-sm text-gray-400 mt-1">
            Modifiez les informations de l'organisation "{organization.organisation_name}"
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Two Section Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Organization Info Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center mb-4">
              <Building2 className="h-4 w-4 text-green-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Informations générales</h3>
            </div>
            
            <div className="space-y-4">
              {/* Organisation Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Nom de l'organisation *
                </label>
                <input
                  type="text"
                  name="organisation_name"
                  value={formData.organisation_name}
                  onChange={handleInputChange}
                  placeholder="Ex: Acme Corp"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                />
              </div>

              {/* Organisation Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="organisation_description"
                  value={formData.organisation_description}
                  onChange={handleInputChange}
                  placeholder="Décrivez l'organisation..."
                  rows="6"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Activation Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center mb-4">
              <Shield className="h-4 w-4 text-purple-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Statut d'activation</h3>
            </div>
            
            <div className="space-y-4">
              {/* Active Status */}
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="text-xs font-medium text-gray-700">Organisation active</span>
                </label>
              </div>

              {/* Deactivation Reason */}
              {!formData.is_active && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Raison de la désactivation *
                  </label>
                  <textarea
                    name="deactivation_reason"
                    value={formData.deactivation_reason}
                    onChange={handleInputChange}
                    placeholder="Expliquez pourquoi cette organisation est désactivée..."
                    rows="3"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={loading}
                    required
                  />
                </div>
              )}

              {/* Status Info */}
              <div className={`p-3 rounded-lg border ${
                formData.is_active 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-xs ${
                  formData.is_active ? 'text-green-700' : 'text-red-700'
                }`}>
                  {formData.is_active 
                    ? 'L\'organisation est active et les utilisateurs peuvent accéder au système.'
                    : 'L\'organisation est désactivée et les utilisateurs ne peuvent pas accéder au système.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {loading ? 'Mise à jour en cours...' : 'Mettre à jour l\'organisation'}
          </button>
        </div>
      </form>
    </div>
  );
}